/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Form,
  Row,
  Col,
  Input,
  Radio,
  Typography,
  Spin,
  DatePicker,
  Select,
  Button,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CustomerApis } from 'apis/CustomerApis';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { GET_REGION } from 'apis/GraphQL/Queries/customerQuery';
import { toastError, toastSuccess } from 'utils/constants/functions';

const { Title } = Typography;
const { TextArea } = Input;

interface IProps {
  sapCustomerId?: string;
  typeSap?: string | number; // 1 = Cá nhân, 2 = Doanh nghiệp
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const JOB_TITLES: Record<string, string> = {
  '000008': 'Tiểu thương',
  '000007': 'Kỹ sư',
  '000005': 'Công nhân',
  '000006': 'Nông dân',
  '000004': 'Hưu trí',
  '000003': 'Lao động tự do',
  '000002': 'Nhân viên văn phòng',
  '000012': 'Nội trợ',
  '000011': 'Bác sĩ',
  '000010': 'Giáo viên',
  '000009': 'Công chức/viên chức',
  '000001': 'Học sinh/sinh viên',
  '000013': 'Khác',
};

const JOB_TITLE_OPTIONS = Object.entries(JOB_TITLES).map(([value, label]) => ({
  value,
  label,
}));

const INDIVIDUAL_IDENTITY_TYPES: Record<string, string> = {
  ID000_01: 'CMTND',
  ID000_02: 'CCCD',
  ID000_03: 'Thẻ căn cước',
  ID000_04: 'Hộ chiếu',
  ID000_05: 'Định danh cá nhân',
  ID000_06: 'Khác',
};

const CORPORATE_IDENTITY_TYPES: Record<string, string> = {
  ID000_02: 'CCCD',
  ID000_03: 'Thẻ căn cước',
  ID000_04: 'Hộ chiếu',
  ID000_05: 'Định danh cá nhân',
  ID000_06: 'Khác',
};

const INDIVIDUAL_IDENTITY_TYPE_OPTIONS = Object.entries(
  INDIVIDUAL_IDENTITY_TYPES,
).map(([value, label]) => ({
  value,
  label,
}));

const CORPORATE_IDENTITY_TYPE_OPTIONS = Object.entries(
  CORPORATE_IDENTITY_TYPES,
).map(([value, label]) => ({
  value,
  label,
}));

const ADDRESS_PREFIXES = [
  'customer',
  'legalRepresentative',
  'director',
  'accountant',
  'beneficiary1',
  'beneficiary2',
];

const Field = ({
  name,
  label,
  placeholder,
  disabled,
  required,
  validateRequired,
  rules,
}: {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  validateRequired?: boolean;
  rules?: any[];
}) => {
  const isRuleRequired =
    validateRequired !== undefined ? validateRequired : required && !disabled;
  const itemRules =
    rules ||
    (isRuleRequired
      ? [
          {
            required: true,
            message: `Vui lòng nhập ${label.toLowerCase()}`,
          },
        ]
      : undefined);

  return (
    <Form.Item
      name={name}
      label={label}
      required={required}
      rules={itemRules}
      style={{ marginBottom: 16 }}
    >
      <Input
        placeholder={placeholder || `Nhập ${label.toLowerCase()}`}
        allowClear={!disabled}
        disabled={disabled}
      />
    </Form.Item>
  );
};

const DateField = ({
  name,
  label,
  disabled,
  required,
  validateRequired,
  rules,
}: {
  name: string;
  label: string;
  disabled?: boolean;
  required?: boolean;
  validateRequired?: boolean;
  rules?: any[];
}) => {
  const isRuleRequired =
    validateRequired !== undefined ? validateRequired : required && !disabled;
  const itemRules =
    rules ||
    (isRuleRequired
      ? [
          {
            required: true,
            message: `Vui lòng chọn ${label.toLowerCase()}`,
          },
        ]
      : undefined);

  return (
    <Form.Item
      name={name}
      label={label}
      required={required}
      rules={itemRules}
      style={{ marginBottom: 16 }}
    >
      <DatePicker
        format="DD/MM/YYYY"
        placeholder="Chọn ngày"
        style={{ width: '100%' }}
        allowClear={!disabled}
        disabled={disabled}
      />
    </Form.Item>
  );
};

export const ModalViewKycPage: React.FC<IProps> = ({
  sapCustomerId,
  typeSap,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const [hl1Option, setHl1Option] = useState<number>(1);
  const [hl1SameCustomer, setHl1SameCustomer] = useState<boolean>(true);
  const [hl2SameCustomer, setHl2SameCustomer] = useState<boolean>(true);

  const cOrgName = Form.useWatch('cOrganizationFullName', form);
  const founderCustName = Form.useWatch('customerName', form);
  const isFounder1RuleActive = Boolean(founderCustName || !cOrgName);
  const isFounder2RuleActive = Boolean(cOrgName || !founderCustName);

  const { data: response, isLoading } = useQuery({
    queryKey: ['getCustomerKycInfo', sapCustomerId],
    queryFn: () => CustomerApis.getCustomerKycInfo(sapCustomerId!),
    enabled: !!sapCustomerId && isOpen,
  });

  const { data: regionResponse } = useQuery({
    queryKey: ['getRegionList'],
    queryFn: () =>
      axiosGraphQL.post('/graphql', {
        query: GET_REGION,
        variables: { take: 5000 },
      }),
    enabled: isOpen,
  });

  const kycData = (response as any)?.data || {};
  const isPersonal =
    typeSap !== undefined && typeSap !== null
      ? String(typeSap) === '1' || String(typeSap) === 'Cá nhân'
      : !kycData?.organizationFullName;
  const regionItems = (regionResponse as any)?.data?.getRegion?.items || [];

  const cityOptions = useMemo(() => {
    const citiesMap: { [key: string]: string } = {};
    regionItems.forEach((item: any) => {
      const cityName = item.cityName || item.tcity?.name;
      if (item.cityId && cityName) {
        citiesMap[String(item.cityId)] = cityName;
      }
    });
    return Object.keys(citiesMap)
      .map((id) => ({
        label: citiesMap[id],
        value: id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
  }, [regionItems]);

  const getWardOptions = (cityId?: string) => {
    if (!cityId) return [];
    const wardsMap: { [key: string]: string } = {};
    regionItems
      .filter((item: any) => String(item.cityId) === String(cityId))
      .forEach((item: any) => {
        const wardName = item.wardName || item.tward?.name;
        if (item.wardId && wardName) {
          wardsMap[String(item.wardId)] = wardName;
        }
      });
    return Object.keys(wardsMap)
      .map((id) => ({
        label: wardsMap[id],
        value: id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
  };

  const findCityId = (val: string) => {
    if (!val) return undefined;
    const str = String(val).trim();
    if (regionItems.some((r: any) => String(r.cityId) === str)) {
      return str;
    }
    const found = regionItems.find(
      (r: any) =>
        (r.cityName || r.tcity?.name || '').toLowerCase() === str.toLowerCase(),
    );
    return found ? String(found.cityId) : str;
  };

  const findWardId = (cityId: string | undefined, val: string) => {
    if (!val) return undefined;
    const str = String(val).trim();
    if (
      regionItems.some(
        (r: any) =>
          (!cityId || String(r.cityId) === String(cityId)) &&
          String(r.wardId) === str,
      )
    ) {
      return str;
    }
    const found = regionItems.find(
      (r: any) =>
        (!cityId || String(r.cityId) === String(cityId)) &&
        (r.wardName || r.tward?.name || '').toLowerCase() === str.toLowerCase(),
    );
    return found ? String(found.wardId) : str;
  };

  const CitySelect = ({
    name,
    label,
    wardName,
    disabled,
    required,
    validateRequired,
  }: {
    name: string;
    label: string;
    wardName?: string;
    disabled?: boolean;
    required?: boolean;
    validateRequired?: boolean;
  }) => {
    const currentCityVal = Form.useWatch(name, form);
    const options = useMemo(() => {
      const list = [...cityOptions];
      if (
        currentCityVal &&
        !list.some((o) => String(o.value) === String(currentCityVal))
      ) {
        list.unshift({
          label: String(currentCityVal),
          value: String(currentCityVal),
        });
      }
      return list;
    }, [currentCityVal]);

    const isRuleRequired =
      validateRequired !== undefined ? validateRequired : required && !disabled;

    return (
      <Form.Item
        name={name}
        label={label}
        required={required}
        rules={
          isRuleRequired
            ? [
                {
                  required: true,
                  message: `Vui lòng chọn ${label.toLowerCase()}`,
                },
              ]
            : undefined
        }
        style={{ marginBottom: 16 }}
      >
        <Select
          placeholder="Chọn tỉnh / thành phố"
          allowClear={!disabled}
          showSearch
          disabled={disabled}
          options={options}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          onChange={() => {
            if (wardName) {
              form.setFieldValue(wardName, undefined);
            }
          }}
        />
      </Form.Item>
    );
  };

  const WardSelect = ({
    name,
    label,
    cityName,
    disabled,
    required,
    validateRequired,
  }: {
    name: string;
    label: string;
    cityName: string;
    disabled?: boolean;
    required?: boolean;
    validateRequired?: boolean;
  }) => {
    const selectedCityId = Form.useWatch(cityName, form);
    const currentWardVal = Form.useWatch(name, form);

    const options = useMemo(() => {
      const list = getWardOptions(selectedCityId);
      if (
        currentWardVal &&
        !list.some((o) => String(o.value) === String(currentWardVal))
      ) {
        list.unshift({
          label: String(currentWardVal),
          value: String(currentWardVal),
        });
      }
      return list;
    }, [selectedCityId, currentWardVal]);

    const isRuleRequired =
      validateRequired !== undefined ? validateRequired : required && !disabled;

    return (
      <Form.Item
        name={name}
        label={label}
        required={required}
        rules={
          isRuleRequired
            ? [
                {
                  required: true,
                  message: `Vui lòng chọn ${label.toLowerCase()}`,
                },
              ]
            : undefined
        }
        style={{ marginBottom: 16 }}
      >
        <Select
          placeholder={
            selectedCityId ? 'Chọn phường / xã' : 'Vui lòng chọn tỉnh trước'
          }
          allowClear={!disabled}
          showSearch
          disabled={disabled || !selectedCityId}
          options={options}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </Form.Item>
    );
  };

  const getSourcePrefix = (option: number | string) => {
    switch (Number(option)) {
      case 1:
        return 'customer';
      case 2:
        return 'legalRepresentative';
      case 3:
        return 'director';
      case 4:
        return 'accountant';
      default:
        return null;
    }
  };

  const copyPersonFields = (sourcePrefix: string, targetPrefix: string) => {
    const currentValues = form.getFieldsValue(true);
    const sourceName =
      sourcePrefix === 'customer'
        ? currentValues.customerName || currentValues.customerFullName
        : currentValues[`${sourcePrefix}Name`];

    const updates: Record<string, any> = {
      [`${targetPrefix}Name`]: sourceName || '',
      [`${targetPrefix}BirthDate`]:
        currentValues[`${sourcePrefix}BirthDate`] || null,
      [`${targetPrefix}Nationality`]:
        currentValues[`${sourcePrefix}Nationality`] || '',
      [`${targetPrefix}OtherCountry`]:
        currentValues[`${sourcePrefix}OtherCountry`] || '',
      [`${targetPrefix}JobTitle`]:
        currentValues[`${sourcePrefix}JobTitle`] ?? undefined,
      [`${targetPrefix}PermanentCity`]:
        currentValues[`${sourcePrefix}PermanentCity`] ?? undefined,
      [`${targetPrefix}PermanentWard`]:
        currentValues[`${sourcePrefix}PermanentWard`] ?? undefined,
      [`${targetPrefix}PermanentCountry`]: 'Việt Nam',
      [`${targetPrefix}PermanentAddress`]:
        currentValues[`${sourcePrefix}PermanentAddress`] || '',
      [`${targetPrefix}CurrentCity`]:
        currentValues[`${sourcePrefix}CurrentCity`] ?? undefined,
      [`${targetPrefix}CurrentWard`]:
        currentValues[`${sourcePrefix}CurrentWard`] ?? undefined,
      [`${targetPrefix}CurrentCountry`]: 'Việt Nam',
      [`${targetPrefix}CurrentAddress`]:
        currentValues[`${sourcePrefix}CurrentAddress`] || '',
      [`${targetPrefix}IdentityType`]:
        currentValues[`${sourcePrefix}IdentityType`] ?? undefined,
      [`${targetPrefix}IdentityNo`]:
        currentValues[`${sourcePrefix}IdentityNo`] || '',
      [`${targetPrefix}IdentityIssueDate`]:
        currentValues[`${sourcePrefix}IdentityIssueDate`] || null,
      [`${targetPrefix}IdentityIssueAuthority`]:
        currentValues[`${sourcePrefix}IdentityIssueAuthority`] || '',
      [`${targetPrefix}IdentityIssuePlace`]:
        currentValues[`${sourcePrefix}IdentityIssuePlace`] || '',
      [`${targetPrefix}VisaNo`]: currentValues[`${sourcePrefix}VisaNo`] || '',
      [`${targetPrefix}VisaIssueDate`]:
        currentValues[`${sourcePrefix}VisaIssueDate`] || null,
      [`${targetPrefix}VisaExpiredDate`]:
        currentValues[`${sourcePrefix}VisaExpiredDate`] || null,
      [`${targetPrefix}VisaIssueAuthority`]:
        currentValues[`${sourcePrefix}VisaIssueAuthority`] || '',
      [`${targetPrefix}VisaIssuePlace`]:
        currentValues[`${sourcePrefix}VisaIssuePlace`] || '',
      [`${targetPrefix}ResidentCardNo`]:
        currentValues[`${sourcePrefix}ResidentCardNo`] || '',
      [`${targetPrefix}ResidentCardIssueDate`]:
        currentValues[`${sourcePrefix}ResidentCardIssueDate`] || null,
      [`${targetPrefix}ResidentCardExpiredDate`]:
        currentValues[`${sourcePrefix}ResidentCardExpiredDate`] || null,
      [`${targetPrefix}ResidentCardIssueAuthority`]:
        currentValues[`${sourcePrefix}ResidentCardIssueAuthority`] || '',
      [`${targetPrefix}ResidentCardIssuePlace`]:
        currentValues[`${sourcePrefix}ResidentCardIssuePlace`] || '',
      [`${targetPrefix}Phone`]: currentValues[`${sourcePrefix}Phone`] || '',
    };

    form.setFieldsValue(updates);
  };

  const clearPersonFields = (targetPrefix: string) => {
    const fieldsToClear = [
      'Name',
      'BirthDate',
      'Nationality',
      'OtherCountry',
      'JobTitle',
      'PermanentCity',
      'PermanentWard',
      'PermanentAddress',
      'CurrentCity',
      'CurrentWard',
      'CurrentAddress',
      'IdentityType',
      'IdentityNo',
      'IdentityIssueDate',
      'IdentityIssueAuthority',
      'IdentityIssuePlace',
      'VisaNo',
      'VisaIssueDate',
      'VisaExpiredDate',
      'VisaIssueAuthority',
      'VisaIssuePlace',
      'ResidentCardNo',
      'ResidentCardIssueDate',
      'ResidentCardExpiredDate',
      'ResidentCardIssueAuthority',
      'ResidentCardIssuePlace',
      'Phone',
    ];
    const updates: Record<string, any> = {
      [`${targetPrefix}PermanentCountry`]: 'Việt Nam',
      [`${targetPrefix}CurrentCountry`]: 'Việt Nam',
    };
    fieldsToClear.forEach((f) => {
      updates[`${targetPrefix}${f}`] = undefined;
    });
    form.setFieldsValue(updates);
  };

  useEffect(() => {
    if (kycData && isOpen) {
      const formValues: any = { ...kycData };

      // Convert all Date fields to dayjs objects
      Object.keys(formValues).forEach((key) => {
        if (key.endsWith('Date') && formValues[key]) {
          const d = dayjs(formValues[key]);
          formValues[key] = d.isValid() ? d : null;
        }
      });

      // Populate customerName from customerFullName if empty
      if (formValues.customerFullName && !formValues.customerName) {
        formValues.customerName = formValues.customerFullName;
      }

      // Map city/ward values to proper cityId/wardId
      if (formValues.organizationCity) {
        formValues.organizationCity = findCityId(formValues.organizationCity);
      }
      if (formValues.organizationWard) {
        formValues.organizationWard = findWardId(
          formValues.organizationCity,
          formValues.organizationWard,
        );
      }
      if (formValues.cOrganizationCity) {
        formValues.cOrganizationCity = findCityId(formValues.cOrganizationCity);
      }
      if (formValues.cOrganizationWard) {
        formValues.cOrganizationWard = findWardId(
          formValues.cOrganizationCity,
          formValues.cOrganizationWard,
        );
      }

      ADDRESS_PREFIXES.forEach((p) => {
        if (formValues[`${p}PermanentCity`]) {
          formValues[`${p}PermanentCity`] = findCityId(
            formValues[`${p}PermanentCity`],
          );
        }
        if (formValues[`${p}PermanentWard`]) {
          formValues[`${p}PermanentWard`] = findWardId(
            formValues[`${p}PermanentCity`],
            formValues[`${p}PermanentWard`],
          );
        }
        if (formValues[`${p}CurrentCity`]) {
          formValues[`${p}CurrentCity`] = findCityId(
            formValues[`${p}CurrentCity`],
          );
        }
        if (formValues[`${p}CurrentWard`]) {
          formValues[`${p}CurrentWard`] = findWardId(
            formValues[`${p}CurrentCity`],
            formValues[`${p}CurrentWard`],
          );
        }
      });

      // Gán cứng Quốc gia là 'Việt Nam'
      formValues.organizationCountry = 'Việt Nam';
      formValues.cOrganizationCountry = 'Việt Nam';
      ADDRESS_PREFIXES.forEach((p) => {
        formValues[`${p}PermanentCountry`] = 'Việt Nam';
        formValues[`${p}CurrentCountry`] = 'Việt Nam';

        let nat = formValues[`${p}Nationality`];
        if (nat !== undefined && nat !== null) {
          nat = String(nat).trim();
          if (
            nat === '0' ||
            nat === 'Việt Nam' ||
            nat.toLowerCase() === 'vn' ||
            nat.toLowerCase() === 'vnm'
          ) {
            formValues[`${p}Nationality`] = '0';
          } else if (nat === '1' || nat === 'Nước ngoài') {
            formValues[`${p}Nationality`] = '1';
          } else if (nat === '2' || nat === 'Không quốc tịch') {
            formValues[`${p}Nationality`] = '2';
          } else {
            if (!formValues[`${p}OtherCountry`]) {
              formValues[`${p}OtherCountry`] = nat;
            }
            formValues[`${p}Nationality`] = '1';
          }
        } else {
          formValues[`${p}Nationality`] = '0';
        }

        const rawIdType = formValues[`${p}IdentityType`];
        if (rawIdType) {
          const s = String(rawIdType).trim().toLowerCase();
          if (s.includes('cmtnd') || s.includes('cmnd') || s === 'id000_01') {
            formValues[`${p}IdentityType`] = isPersonal
              ? 'ID000_01'
              : undefined;
          } else if (s === 'cccd' || s === 'id000_02') {
            formValues[`${p}IdentityType`] = 'ID000_02';
          } else if (
            s.includes('thẻ căn cước') ||
            s.includes('the can cuoc') ||
            s === 'id000_03'
          ) {
            formValues[`${p}IdentityType`] = 'ID000_03';
          } else if (
            s.includes('hộ chiếu') ||
            s.includes('ho chieu') ||
            s === 'id000_04'
          ) {
            formValues[`${p}IdentityType`] = 'ID000_04';
          } else if (
            s.includes('định danh') ||
            s.includes('dinh danh') ||
            s === 'id000_05'
          ) {
            formValues[`${p}IdentityType`] = 'ID000_05';
          } else if (
            s.includes('khác') ||
            s.includes('khac') ||
            s === 'id000_06'
          ) {
            formValues[`${p}IdentityType`] = 'ID000_06';
          }
        }
      });

      // Beneficiary options mapping
      let initHl1Opt = 1;
      if (!isPersonal) {
        if (
          formValues.beneficiary1SameAsOption !== undefined &&
          formValues.beneficiary1SameAsOption !== null
        ) {
          const opt = parseInt(String(formValues.beneficiary1SameAsOption), 10);
          initHl1Opt = isNaN(opt) ? 1 : opt;
        } else if (formValues.beneficiary1SameAsCustomer === true) {
          initHl1Opt = 1;
        } else {
          initHl1Opt = 1;
        }
        formValues.beneficiary1SameAsOption = initHl1Opt;
        setHl1Option(initHl1Opt);
      } else {
        const s1 =
          formValues.beneficiary1SameAsCustomer === false ||
          String(formValues.beneficiary1SameAsCustomer) === 'false'
            ? false
            : true;
        const s2 =
          formValues.beneficiary2SameAsCustomer === false ||
          String(formValues.beneficiary2SameAsCustomer) === 'false'
            ? false
            : true;
        formValues.beneficiary1SameAsCustomer = s1;
        formValues.beneficiary2SameAsCustomer = s2;
        setHl1SameCustomer(s1);
        setHl2SameCustomer(s2);
      }

      const copyValuesFromSource = (
        sourcePrefix: string,
        targetPrefix: string,
      ) => {
        const sourceName =
          sourcePrefix === 'customer'
            ? formValues.customerName || formValues.customerFullName
            : formValues[`${sourcePrefix}Name`];

        formValues[`${targetPrefix}Name`] = sourceName || '';
        formValues[`${targetPrefix}BirthDate`] =
          formValues[`${sourcePrefix}BirthDate`] || null;
        formValues[`${targetPrefix}Nationality`] =
          formValues[`${sourcePrefix}Nationality`] || '';
        formValues[`${targetPrefix}OtherCountry`] =
          formValues[`${sourcePrefix}OtherCountry`] || '';
        formValues[`${targetPrefix}JobTitle`] =
          formValues[`${sourcePrefix}JobTitle`] ?? undefined;
        formValues[`${targetPrefix}PermanentCity`] =
          formValues[`${sourcePrefix}PermanentCity`] ?? undefined;
        formValues[`${targetPrefix}PermanentWard`] =
          formValues[`${sourcePrefix}PermanentWard`] ?? undefined;
        formValues[`${targetPrefix}PermanentCountry`] = 'Việt Nam';
        formValues[`${targetPrefix}PermanentAddress`] =
          formValues[`${sourcePrefix}PermanentAddress`] || '';
        formValues[`${targetPrefix}CurrentCity`] =
          formValues[`${sourcePrefix}CurrentCity`] ?? undefined;
        formValues[`${targetPrefix}CurrentWard`] =
          formValues[`${sourcePrefix}CurrentWard`] ?? undefined;
        formValues[`${targetPrefix}CurrentCountry`] = 'Việt Nam';
        formValues[`${targetPrefix}CurrentAddress`] =
          formValues[`${sourcePrefix}CurrentAddress`] || '';
        formValues[`${targetPrefix}IdentityType`] =
          formValues[`${sourcePrefix}IdentityType`] ?? undefined;
        formValues[`${targetPrefix}IdentityNo`] =
          formValues[`${sourcePrefix}IdentityNo`] || '';
        formValues[`${targetPrefix}IdentityIssueDate`] =
          formValues[`${sourcePrefix}IdentityIssueDate`] || null;
        formValues[`${targetPrefix}IdentityIssueAuthority`] =
          formValues[`${sourcePrefix}IdentityIssueAuthority`] || '';
        formValues[`${targetPrefix}IdentityIssuePlace`] =
          formValues[`${sourcePrefix}IdentityIssuePlace`] || '';
        formValues[`${targetPrefix}VisaNo`] =
          formValues[`${sourcePrefix}VisaNo`] || '';
        formValues[`${targetPrefix}VisaIssueDate`] =
          formValues[`${sourcePrefix}VisaIssueDate`] || null;
        formValues[`${targetPrefix}VisaExpiredDate`] =
          formValues[`${sourcePrefix}VisaExpiredDate`] || null;
        formValues[`${targetPrefix}VisaIssueAuthority`] =
          formValues[`${sourcePrefix}VisaIssueAuthority`] || '';
        formValues[`${targetPrefix}VisaIssuePlace`] =
          formValues[`${sourcePrefix}VisaIssuePlace`] || '';
        formValues[`${targetPrefix}ResidentCardNo`] =
          formValues[`${sourcePrefix}ResidentCardNo`] || '';
        formValues[`${targetPrefix}ResidentCardIssueDate`] =
          formValues[`${sourcePrefix}ResidentCardIssueDate`] || null;
        formValues[`${targetPrefix}ResidentCardExpiredDate`] =
          formValues[`${sourcePrefix}ResidentCardExpiredDate`] || null;
        formValues[`${targetPrefix}ResidentCardIssueAuthority`] =
          formValues[`${sourcePrefix}ResidentCardIssueAuthority`] || '';
        formValues[`${targetPrefix}ResidentCardIssuePlace`] =
          formValues[`${sourcePrefix}ResidentCardIssuePlace`] || '';
        formValues[`${targetPrefix}Phone`] =
          formValues[`${sourcePrefix}Phone`] || '';
      };

      if (!isPersonal) {
        if (initHl1Opt >= 1 && initHl1Opt <= 4) {
          const src = getSourcePrefix(initHl1Opt);
          if (src) {
            copyValuesFromSource(src, 'beneficiary1');
          }
        }
      } else {
        if (formValues.beneficiary1SameAsCustomer !== false) {
          copyValuesFromSource('customer', 'beneficiary1');
        }
        if (formValues.beneficiary2SameAsCustomer !== false) {
          copyValuesFromSource('customer', 'beneficiary2');
        }
      }

      form.setFieldsValue(formValues);
    } else if (!isOpen) {
      form.resetFields();
    }
  }, [kycData, isOpen, form, regionItems]);

  const handleFinish = async (values: any) => {
    try {
      setIsSaving(true);
      const payload: any = { ...values };

      // Format Date values back to YYYY-MM-DD
      Object.keys(payload).forEach((key) => {
        if (dayjs.isDayjs(payload[key])) {
          payload[key] = payload[key].format('YYYY-MM-DD');
        } else if (payload[key] === undefined) {
          payload[key] = null;
        }
      });

      // Sync customerFullName with customerName
      if (payload.customerName) {
        payload.customerFullName = payload.customerName;
      }

      // Gán cứng Quốc gia là 'Việt Nam'
      payload.organizationCountry = 'Việt Nam';
      payload.cOrganizationCountry = 'Việt Nam';
      ADDRESS_PREFIXES.forEach((p) => {
        payload[`${p}PermanentCountry`] = 'Việt Nam';
        payload[`${p}CurrentCountry`] = 'Việt Nam';
      });

      // Đảm bảo nationality luôn lưu mã số '0', '1', '2'
      [
        'customer',
        'legalRepresentative',
        'director',
        'accountant',
        'beneficiary1',
        'beneficiary2',
      ].forEach((p) => {
        const val = payload[`${p}Nationality`];
        if (val === '0' || val === 'Việt Nam') payload[`${p}Nationality`] = '0';
        else if (val === '1' || val === 'Nước ngoài')
          payload[`${p}Nationality`] = '1';
        else if (val === '2' || val === 'Không quốc tịch')
          payload[`${p}Nationality`] = '2';
      });

      const res: any = await CustomerApis.updateCustomerKycInfo(
        sapCustomerId!,
        payload,
      );

      if (res?.data || res?.message || res?.status === 200) {
        toastSuccess('Cập nhật thông tin khách hàng thành công');
        queryClient.invalidateQueries({
          queryKey: ['getCustomerKycInfo', sapCustomerId],
        });
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        toastError(res?.message || 'Cập nhật thất bại');
      }
    } catch (error: any) {
      console.error(error);
      toastError(
        error?.response?.data?.message ||
          error?.message ||
          'Có lỗi xảy ra khi cập nhật',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderSectionHeader = (
    title: string,
    size: 4 | 5 = 5,
    required = false,
  ) => (
    <Title
      level={size}
      style={{ marginTop: 24, marginBottom: 16, fontWeight: 600 }}
    >
      {title}
      {required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
    </Title>
  );

  const renderCreatorInfo = () => (
    <>
      {renderSectionHeader('Người lập phiếu')}
      <Row gutter={16}>
        <Col span={8}>
          <Field name="creatorName" label="Họ và tên" />
        </Col>
        <Col span={8}>
          <Field name="creatorPosition" label="Chức danh" />
        </Col>
        <Col span={8}>
          <Field name="creatorDepartment" label="Bộ phận/ Đơn vị" />
        </Col>
      </Row>
    </>
  );

  const NationalityGroup = ({
    prefix,
    disabled,
    required,
    validateRequired,
  }: {
    prefix: string;
    disabled?: boolean;
    required?: boolean;
    validateRequired?: boolean;
  }) => {
    const currentNat = Form.useWatch(`${prefix}Nationality`, form);
    const isOther =
      String(currentNat) === '1' ||
      currentNat === 'Nước ngoài' ||
      (currentNat &&
        String(currentNat) !== '0' &&
        currentNat !== 'Việt Nam' &&
        String(currentNat) !== '2' &&
        currentNat !== 'Không quốc tịch');

    const isRuleRequired =
      validateRequired !== undefined ? validateRequired : required && !disabled;

    return (
      <Row gutter={16}>
        <Col span={14}>
          <Form.Item
            name={`${prefix}Nationality`}
            label="Quốc tịch"
            required={required}
            rules={
              isRuleRequired
                ? [{ required: true, message: 'Vui lòng chọn quốc tịch' }]
                : undefined
            }
            style={{ marginBottom: 16 }}
          >
            <Radio.Group
              disabled={disabled}
              onChange={(e) => {
                if (String(e.target.value) !== '1') {
                  form.setFieldValue(`${prefix}OtherCountry`, undefined);
                }
              }}
            >
              <Radio value="0">Việt Nam</Radio>
              <Radio value="1">Nước ngoài</Radio>
              <Radio value="2">Không quốc tịch</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={10}>
          <Field
            name={`${prefix}OtherCountry`}
            label="Nước ngoài (Tên quốc gia)"
            placeholder={
              isOther
                ? 'Nhập tên quốc gia (ví dụ: Mỹ)'
                : 'Chỉ nhập khi là Nước ngoài'
            }
            disabled={disabled || !isOther}
            required={required && isOther}
            validateRequired={isRuleRequired && isOther}
          />
        </Col>
      </Row>
    );
  };

  const renderPersonFields = (
    prefix: string,
    disabled?: boolean,
    isRequired = true,
    validateRequired?: boolean,
  ) => {
    const isBeneficiary = prefix.startsWith('beneficiary');
    const isRuleActive =
      validateRequired !== undefined
        ? validateRequired
        : isRequired && !disabled;
    const nameField = prefix === 'customer' ? 'customerName' : `${prefix}Name`;
    const isJobRequired =
      isRequired && prefix !== 'director' && prefix !== 'accountant';
    const isJobRuleActive =
      validateRequired !== undefined
        ? validateRequired && prefix !== 'director' && prefix !== 'accountant'
        : isJobRequired && !disabled;

    return (
      <>
        <Row gutter={16}>
          <Col span={prefix === 'director' || prefix === 'accountant' ? 12 : 8}>
            <Field
              name={nameField}
              label="Họ và tên"
              disabled={disabled}
              required={isRequired}
              validateRequired={isRuleActive}
            />
          </Col>
          <Col span={prefix === 'director' || prefix === 'accountant' ? 12 : 8}>
            <DateField
              name={`${prefix}BirthDate`}
              label="Ngày, tháng, năm sinh"
              disabled={disabled}
              required={isRequired}
              validateRequired={isRuleActive}
            />
          </Col>
          {prefix !== 'director' && prefix !== 'accountant' && (
            <Col span={8}>
              <Form.Item
                name={`${prefix}JobTitle`}
                label="Nghề nghiệp, chức vụ"
                style={{ marginBottom: 16 }}
                required={isJobRequired}
                rules={
                  isJobRuleActive
                    ? [
                        {
                          required: !disabled,
                          message: 'Vui lòng chọn nghề nghiệp, chức vụ',
                        },
                      ]
                    : undefined
                }
              >
                <Select
                  placeholder="Chọn nghề nghiệp, chức vụ"
                  allowClear={!disabled}
                  showSearch
                  disabled={disabled}
                  options={JOB_TITLE_OPTIONS}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <NationalityGroup
          prefix={prefix}
          disabled={disabled}
          required={isRequired}
          validateRequired={isRuleActive}
        />
        <Row gutter={16}>
          <Col span={4}>
            <Field
              name={`${prefix}PermanentCountry`}
              label="Quốc gia thường trú"
              disabled
            />
          </Col>
          <Col span={6}>
            <CitySelect
              name={`${prefix}PermanentCity`}
              label="Tỉnh / Thành phố thường trú"
              wardName={`${prefix}PermanentWard`}
              disabled={disabled}
              required={isRequired}
              validateRequired={isRuleActive}
            />
          </Col>
          <Col span={6}>
            <WardSelect
              name={`${prefix}PermanentWard`}
              label="Phường / Xã thường trú"
              cityName={`${prefix}PermanentCity`}
              disabled={disabled}
              required={isRequired}
              validateRequired={isRuleActive}
            />
          </Col>
          <Col span={8}>
            <Field
              name={`${prefix}PermanentAddress`}
              label="Địa chỉ thường trú"
              disabled={disabled}
              required={isRequired}
              validateRequired={isRuleActive}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Field
              name={`${prefix}CurrentCountry`}
              label="Quốc gia hiện ở"
              disabled
            />
          </Col>
          <Col span={6}>
            <CitySelect
              name={`${prefix}CurrentCity`}
              label="Tỉnh / Thành phố hiện ở"
              wardName={`${prefix}CurrentWard`}
              disabled={disabled}
            />
          </Col>
          <Col span={6}>
            <WardSelect
              name={`${prefix}CurrentWard`}
              label="Phường / Xã hiện ở"
              cityName={`${prefix}CurrentCity`}
              disabled={disabled}
            />
          </Col>
          <Col span={8}>
            <Field
              name={`${prefix}CurrentAddress`}
              label="Địa chỉ hiện ở"
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={`${prefix}IdentityType`}
              label="Thông tin định danh cá nhân"
              style={{ marginBottom: 16 }}
              required={isRequired}
              rules={
                isRuleActive
                  ? [
                      {
                        required: !disabled,
                        message: 'Vui lòng chọn thông tin định danh cá nhân',
                      },
                    ]
                  : undefined
              }
            >
              <Select
                placeholder="Chọn thông tin định danh"
                allowClear={!disabled}
                showSearch
                disabled={disabled}
                options={
                  isPersonal
                    ? INDIVIDUAL_IDENTITY_TYPE_OPTIONS
                    : CORPORATE_IDENTITY_TYPE_OPTIONS
                }
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Field
              name={`${prefix}IdentityNo`}
              label="Số thông tin định danh cá nhân"
              disabled={disabled}
              required={isRequired}
              validateRequired={isRuleActive}
            />
          </Col>
          <Col span={8}>
            <DateField
              name={`${prefix}IdentityIssueDate`}
              label="Ngày cấp thông tin định danh cá nhân"
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Field
              name={`${prefix}IdentityIssueAuthority`}
              label="Cơ quan có thẩm quyền cấp thông tin định danh cá nhân"
              disabled={disabled}
            />
          </Col>
          <Col span={16}>
            <Field
              name={`${prefix}IdentityIssuePlace`}
              label="Nơi cấp thông tin định danh cá nhân"
              disabled={disabled}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Field
              name={`${prefix}VisaNo`}
              label="Số thị thực nhập cảnh"
              disabled={disabled}
            />
          </Col>
          <Col span={8}>
            <DateField
              name={`${prefix}VisaIssueDate`}
              label="Ngày cấp thị thực nhập cảnh"
              disabled={disabled}
            />
          </Col>
          <Col span={8}>
            <DateField
              name={`${prefix}VisaExpiredDate`}
              label="Ngày hết hạn thị thực nhập cảnh"
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Field
              name={`${prefix}VisaIssueAuthority`}
              label="Cơ quan có thẩm quyền cấp thị thực nhập cảnh"
              disabled={disabled}
            />
          </Col>
          <Col span={16}>
            <Field
              name={`${prefix}VisaIssuePlace`}
              label="Nơi cấp thị thực nhập cảnh"
              disabled={disabled}
            />
          </Col>
        </Row>

        {(isPersonal || isBeneficiary) && (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Field
                  name={`${prefix}ResidentCardNo`}
                  label="Số giấy tờ có giá trị đi lại quốc tế/thẻ cư trú"
                  disabled={disabled}
                />
              </Col>
              <Col span={8}>
                <DateField
                  name={`${prefix}ResidentCardIssueDate`}
                  label="Ngày cấp giấy tờ có giá trị đi lại quốc tế/thẻ cư trú"
                  disabled={disabled}
                />
              </Col>
              <Col span={8}>
                <DateField
                  name={`${prefix}ResidentCardExpiredDate`}
                  label="Ngày hết hạn giấy tờ có giá trị đi lại quốc tế/thẻ cư trú"
                  disabled={disabled}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Field
                  name={`${prefix}ResidentCardIssueAuthority`}
                  label="Cơ quan cấp giấy tờ có giá trị đi lại quốc tế/thẻ cư trú"
                  disabled={disabled}
                />
              </Col>
              <Col span={8}>
                <Field
                  name={`${prefix}ResidentCardIssuePlace`}
                  label="Nơi cấp giấy tờ có giá trị đi lại quốc tế/thẻ cư trú"
                  disabled={disabled}
                />
              </Col>
              <Col span={8}>
                <Field
                  name={`${prefix}Phone`}
                  label="Số điện thoại liên lạc"
                  disabled={disabled}
                  required={isRequired}
                  validateRequired={isRuleActive}
                />
              </Col>
            </Row>
          </>
        )}
        {!(isPersonal || isBeneficiary) && (
          <Row gutter={16}>
            <Col span={8}>
              <Field
                name={`${prefix}Phone`}
                label="Số điện thoại liên lạc"
                disabled={disabled}
                required={isRequired}
                validateRequired={isRuleActive}
              />
            </Col>
          </Row>
        )}
      </>
    );
  };

  const renderPersonalCustomer = () => (
    <>
      {renderSectionHeader('1. Thông tin nhận dạng khách hàng')}
      {renderPersonFields('customer', false, true)}

      <Row gutter={16}>
        <Col span={12}>
          <Field name="customerBankAccount" label="Số tài khoản ngân hàng" />
        </Col>
        <Col span={12}>
          <Field name="customerBankName" label="Tên ngân hàng mở tài khoản" />
        </Col>
      </Row>

      {renderSectionHeader('2. Thông tin về chủ sở hữu hưởng lợi', 5, true)}
      {renderSectionHeader('2.1 Chủ sở hữu hưởng lợi thứ nhất', 5)}
      <Form.Item name="beneficiary1SameAsCustomer" style={{ marginBottom: 16 }}>
        <Radio.Group
          value={hl1SameCustomer}
          onChange={(e) => {
            const val =
              e.target.value === true || String(e.target.value) === 'true';
            setHl1SameCustomer(val);
            form.setFieldValue('beneficiary1SameAsCustomer', val);
            if (val) {
              copyPersonFields('customer', 'beneficiary1');
            } else {
              clearPersonFields('beneficiary1');
            }
          }}
        >
          <Radio value={true}>Trùng với cá nhân tại mục 1</Radio>
          <Radio value={false}>Khác</Radio>
        </Radio.Group>
      </Form.Item>
      {renderPersonFields(
        'beneficiary1',
        hl1SameCustomer !== false,
        !hl1SameCustomer,
      )}

      {renderSectionHeader('2.2 Chủ sở hữu hưởng lợi thứ hai', 5)}
      <Form.Item name="beneficiary2SameAsCustomer" style={{ marginBottom: 16 }}>
        <Radio.Group
          value={hl2SameCustomer}
          onChange={(e) => {
            const val =
              e.target.value === true || String(e.target.value) === 'true';
            setHl2SameCustomer(val);
            form.setFieldValue('beneficiary2SameAsCustomer', val);
            if (val) {
              copyPersonFields('customer', 'beneficiary2');
            } else {
              clearPersonFields('beneficiary2');
            }
          }}
        >
          <Radio value={true}>Trùng với cá nhân tại mục 1</Radio>
          <Radio value={false}>Khác</Radio>
        </Radio.Group>
      </Form.Item>
      {renderPersonFields(
        'beneficiary2',
        hl2SameCustomer !== false,
        !hl2SameCustomer,
      )}
    </>
  );

  const renderEnterpriseCustomer = () => (
    <>
      {renderSectionHeader('1. Thông tin nhận dạng khách hàng')}
      {renderSectionHeader('1.1 Thông tin về tổ chức', 5, true)}
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="organizationFullName"
            label="Tên đầy đủ của tổ chức"
            required
          />
        </Col>
        <Col span={8}>
          <Field
            name="organizationForeignName"
            label="Tên tiếng nước ngoài (nếu có)"
          />
        </Col>
        <Col span={8}>
          <Field name="organizationShortName" label="Tên viết tắt (nếu có)" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={4}>
          <Field name="organizationCountry" label="Quốc gia" disabled />
        </Col>
        <Col span={6}>
          <CitySelect
            name="organizationCity"
            label="Tỉnh / Thành phố"
            wardName="organizationWard"
            required
          />
        </Col>
        <Col span={6}>
          <WardSelect
            name="organizationWard"
            label="Phường / Xã"
            cityName="organizationCity"
            required
          />
        </Col>
        <Col span={8}>
          <Field
            name="organizationAddress"
            label="Địa chỉ trụ sở chính"
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="organizationLicenseNo"
            label="Giấy phép thành lập số/ Mã số doanh nghiệp/ MST"
            required
          />
        </Col>
        <Col span={8}>
          <DateField name="organizationLicenseDate" label="Ngày cấp" />
        </Col>
        <Col span={8}>
          <Field name="organizationLicensePlace" label="Nơi cấp" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="organizationBusinessField"
            label="Lĩnh vực hoạt động, kinh doanh"
            required
          />
        </Col>
        <Col span={8}>
          <Field
            name="organizationPhone"
            label="Số điện thoại liên lạc"
            required
          />
        </Col>
        <Col span={8}>
          <Field
            name="organizationWebsite"
            label="Địa chỉ trang thông tin điện tử của doanh nghiệp"
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="organizationBankAccount"
            label="Số tài khoản ngân hàng"
          />
        </Col>
        <Col span={16}>
          <Field
            name="organizationBankName"
            label="Tên ngân hàng mở tài khoản"
          />
        </Col>
      </Row>

      {renderSectionHeader('1.2 Thông tin về người thành lập tổ chức', 5, true)}
      {renderSectionHeader('1.2.1 Trường hợp người thành lập là cá nhân', 5)}
      {renderPersonFields('customer', false, true, isFounder1RuleActive)}

      {renderSectionHeader('1.2.2 Trường hợp người thành lập là tổ chức', 5)}
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="cOrganizationFullName"
            label="Tên đầy đủ của tổ chức"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
        <Col span={8}>
          <Field
            name="cOrganizationForeignName"
            label="Tên tiếng nước ngoài (nếu có)"
          />
        </Col>
        <Col span={8}>
          <Field name="cOrganizationShortName" label="Tên viết tắt (nếu có)" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={4}>
          <Field name="cOrganizationCountry" label="Quốc gia trụ sở" disabled />
        </Col>
        <Col span={6}>
          <CitySelect
            name="cOrganizationCity"
            label="Tỉnh / Thành phố trụ sở"
            wardName="cOrganizationWard"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
        <Col span={6}>
          <WardSelect
            name="cOrganizationWard"
            label="Phường / Xã trụ sở"
            cityName="cOrganizationCity"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
        <Col span={8}>
          <Field
            name="cOrganizationAddress"
            label="Địa chỉ trụ sở chính"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="cOrganizationLicenseNo"
            label="Giấy phép thành lập số/ Mã số doanh nghiệp/ MST"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
        <Col span={8}>
          <DateField name="cOrganizationLicenseDate" label="Ngày cấp" />
        </Col>
        <Col span={8}>
          <Field name="cOrganizationLicensePlace" label="Nơi cấp" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Field
            name="cOrganizationBusinessField"
            label="Lĩnh vực hoạt động, kinh doanh"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
        <Col span={8}>
          <Field
            name="cOrganizationPhone"
            label="Số điện thoại liên lạc"
            required
            validateRequired={isFounder2RuleActive}
          />
        </Col>
        <Col span={8}>
          <Field
            name="cOrganizationWebsite"
            label="Địa chỉ trang thông tin điện tử của doanh nghiệp"
          />
        </Col>
      </Row>

      {renderSectionHeader(
        '1.3 Thông tin về người đại diện theo pháp luật của tổ chức',
        5,
        true,
      )}
      {renderPersonFields('legalRepresentative', false, true)}

      {renderSectionHeader(
        '1.4 Thông tin về Giám đốc / Tổng Giám đốc',
        5,
        true,
      )}
      {renderPersonFields('director', false, true)}

      {renderSectionHeader(
        '1.5 Thông tin về Kế toán trưởng hoặc người phụ trách kế toán',
        5,
        true,
      )}
      {renderPersonFields('accountant', false, true)}

      {renderSectionHeader('2. Thông tin về chủ sở hữu hưởng lợi', 5, true)}
      {renderSectionHeader('2.1 Chủ sở hữu hưởng lợi', 5)}
      <Form.Item name="beneficiary1SameAsOption" style={{ marginBottom: 16 }}>
        <Radio.Group
          value={hl1Option}
          onChange={(e) => {
            const val = Number(e.target.value);
            setHl1Option(val);
            form.setFieldValue('beneficiary1SameAsOption', val);
            const src = getSourcePrefix(val);
            if (src) {
              copyPersonFields(src, 'beneficiary1');
            } else {
              clearPersonFields('beneficiary1');
            }
          }}
        >
          <Radio value={1}>Trùng với cá nhân tại mục 1.2.1</Radio>
          <Radio value={2}>Trùng với cá nhân tại mục 1.3</Radio>
          <Radio value={3}>Trùng với cá nhân tại mục 1.4</Radio>
          <Radio value={4}>Trùng với cá nhân tại mục 1.5</Radio>
          <Radio value={5}>Khác</Radio>
        </Radio.Group>
      </Form.Item>
      {renderPersonFields('beneficiary1', hl1Option !== 5, hl1Option === 5)}
    </>
  );

  return (
    <Modal
      title={
        isPersonal
          ? 'Cập nhật thông tin khách hàng - Cá nhân'
          : 'Cập nhật thông tin khách hàng - Tổ chức'
      }
      open={isOpen}
      onCancel={onClose}
      width={1200}
      style={{ top: 20 }}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isSaving}>
          Hủy
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={() => form.submit()}
          loading={isSaving}
        >
          Lưu thay đổi
        </Button>,
      ]}
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          className="kyc-edit-form"
          onValuesChange={(changedValues) => {
            const syncField = (
              sourcePrefix: string,
              targetPrefix: string,
              suffix: string,
            ) => {
              const sourceKey =
                sourcePrefix === 'customer' && suffix === 'Name'
                  ? 'customerName'
                  : `${sourcePrefix}${suffix}`;
              const altSourceKey =
                sourcePrefix === 'customer' && suffix === 'Name'
                  ? 'customerFullName'
                  : null;
              const targetKey = `${targetPrefix}${suffix}`;
              if (sourceKey in changedValues) {
                form.setFieldValue(targetKey, changedValues[sourceKey]);
              } else if (altSourceKey && altSourceKey in changedValues) {
                form.setFieldValue(targetKey, changedValues[altSourceKey]);
              }
            };

            const syncAllFieldsFor = (
              sourcePrefix: string,
              targetPrefix: string,
            ) => {
              const suffixes = [
                'Name',
                'BirthDate',
                'Nationality',
                'OtherCountry',
                'JobTitle',
                'PermanentCity',
                'PermanentWard',
                'PermanentAddress',
                'CurrentCity',
                'CurrentWard',
                'CurrentAddress',
                'IdentityType',
                'IdentityNo',
                'IdentityIssueDate',
                'IdentityIssueAuthority',
                'IdentityIssuePlace',
                'VisaNo',
                'VisaIssueDate',
                'VisaExpiredDate',
                'VisaIssueAuthority',
                'VisaIssuePlace',
                'ResidentCardNo',
                'ResidentCardIssueDate',
                'ResidentCardExpiredDate',
                'ResidentCardIssueAuthority',
                'ResidentCardIssuePlace',
                'Phone',
              ];
              suffixes.forEach((s) => syncField(sourcePrefix, targetPrefix, s));
            };

            if (!isPersonal) {
              if (hl1Option >= 1 && hl1Option <= 4) {
                const src = getSourcePrefix(hl1Option);
                if (src) {
                  syncAllFieldsFor(src, 'beneficiary1');
                }
              }
            } else {
              if (hl1SameCustomer) {
                syncAllFieldsFor('customer', 'beneficiary1');
              }
              if (hl2SameCustomer) {
                syncAllFieldsFor('customer', 'beneficiary2');
              }
            }
          }}
          initialValues={{
            organizationCountry: 'Việt Nam',
            cOrganizationCountry: 'Việt Nam',
            customerPermanentCountry: 'Việt Nam',
            customerCurrentCountry: 'Việt Nam',
            legalRepresentativePermanentCountry: 'Việt Nam',
            legalRepresentativeCurrentCountry: 'Việt Nam',
            directorPermanentCountry: 'Việt Nam',
            directorCurrentCountry: 'Việt Nam',
            accountantPermanentCountry: 'Việt Nam',
            accountantCurrentCountry: 'Việt Nam',
            beneficiary1PermanentCountry: 'Việt Nam',
            beneficiary1CurrentCountry: 'Việt Nam',
            beneficiary2PermanentCountry: 'Việt Nam',
            beneficiary2CurrentCountry: 'Việt Nam',
            customerNationality: '0',
            legalRepresentativeNationality: '0',
            directorNationality: '0',
            accountantNationality: '0',
            beneficiary1Nationality: '0',
            beneficiary2Nationality: '0',
            beneficiary1SameAsOption: 1,
            beneficiary1SameAsCustomer: true,
            beneficiary2SameAsCustomer: true,
          }}
        >
          {renderCreatorInfo()}
          {isPersonal ? renderPersonalCustomer() : renderEnterpriseCustomer()}

          <Title
            level={5}
            style={{ marginTop: 24, marginBottom: 16, fontWeight: 600 }}
          >
            {isPersonal
              ? '3. Mục đích và bản chất của mối quan hệ kinh doanh'
              : '4. Mục đích và bản chất của mối quan hệ kinh doanh'}
            <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
          </Title>
          <Form.Item
            name="businessRelationshipPurpose"
            rules={[
              {
                required: true,
                message:
                  'Vui lòng nhập mục đích và bản chất của mối quan hệ kinh doanh',
              },
            ]}
            style={{ marginBottom: 24 }}
          >
            <TextArea
              rows={4}
              placeholder="Nhập mục đích và bản chất của mối quan hệ kinh doanh"
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};
